import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Mail, Users, Send, Shield, Building2, FileText, RefreshCw, ExternalLink } from "lucide-react";

const Admin = () => {
  const [token, setToken] = useState(localStorage.getItem("wasntme_admin_token") || "");
  const [authed, setAuthed] = useState(false);
  const [data, setData] = useState(null);
  const [quotesData, setQuotesData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resendingId, setResendingId] = useState("");
  const [blast, setBlast] = useState({ subject: "", html: "", case_interest: "", test_only: true, admin_test_to: "" });
  const [sending, setSending] = useState(false);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [drafts, setDrafts] = useState(null);
  const [smsUsage, setSmsUsage] = useState(null);
  const [jevFlags, setJevFlags] = useState(null);
  const [makeAnalytics, setMakeAnalytics] = useState(null);

  const authHeaders = { headers: { "x-admin-token": token } };

  const loadQuotes = async () => {
    try {
      const r = await api.get("/admin/quotes", authHeaders);
      setQuotesData(r.data);
    } catch (err) {
      // Quotes are optional panel; failure already surfaces via the main unlock flow.
    }
  };

  const loadSettings = async () => {
    try {
      const r = await api.get("/admin/settings", authHeaders);
      setSettings(r.data);
    } catch (err) {
      // non-fatal
    }
  };

  const saveSetting = async (key, data) => {
    setSavingSettings(true);
    try {
      await api.put("/admin/settings", { key, data }, authHeaders);
      toast.success(`${key} saved`);
      setSettings((s) => ({ ...s, [key]: data }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSavingSettings(false);
    }
  };

  const loadDrafts = async () => {
    try {
      const r = await api.get("/admin/social-drafts", authHeaders);
      setDrafts(r.data);
    } catch (err) {
      /* non-fatal */
    }
  };

  const loadSmsUsage = async () => {
    try {
      const r = await api.get("/admin/sms-usage", authHeaders);
      setSmsUsage(r.data);
    } catch (err) {
      /* non-fatal */
    }
  };

  const loadJevFlags = async () => {
    try {
      const r = await api.get("/admin/jev-flags", authHeaders);
      setJevFlags(r.data);
    } catch (err) {
      /* non-fatal */
    }
  };

  const loadMakeAnalytics = async () => {
    try {
      const r = await api.get("/admin/make/analytics", authHeaders);
      setMakeAnalytics(r.data);
    } catch (err) {
      /* non-fatal */
    }
  };

  const loadLeads = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/leads", authHeaders);
      setData(r.data);
      setAuthed(true);
      localStorage.setItem("wasntme_admin_token", token);
      loadQuotes();
      loadSettings();
      loadDrafts();
      loadSmsUsage();
      loadJevFlags();
      loadMakeAnalytics();
    } catch (err) {
      toast.error("Invalid admin token");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadLeads();
  }, []); // eslint-disable-line

  const sendBlast = async (e) => {
    e.preventDefault();
    if (!blast.subject || !blast.html) {
      toast.error("Subject and body required");
      return;
    }
    if (!blast.test_only && !window.confirm(`Send to ${estimatedRecipients()} real leads? This cannot be undone.`)) return;
    setSending(true);
    try {
      const r = await api.post("/admin/leads/blast", {
        subject: blast.subject,
        html: blast.html,
        case_interest: blast.case_interest || null,
        test_only: blast.test_only,
        admin_test_to: blast.test_only ? blast.admin_test_to : null,
      }, authHeaders);
      toast.success(`Sent to ${r.data.sent}, failed ${r.data.failed}, recipients ${r.data.recipients}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Blast failed");
    } finally {
      setSending(false);
    }
  };

  const estimatedRecipients = () => {
    if (!data) return 0;
    if (!blast.case_interest) return data.total;
    return data.by_case_interest[blast.case_interest] || 0;
  };

  const resendQuote = async (quoteId) => {
    setResendingId(quoteId);
    try {
      await api.post(`/admin/quotes/${quoteId}/resend`, {}, authHeaders);
      toast.success("Invoice re-sent");
      loadQuotes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Resend failed");
    } finally {
      setResendingId("");
    }
  };

  const logoUrl = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/business/logo/${p}`;

  const markPosted = async (draftId, channel = "both") => {
    const posted_url = window.prompt("Paste the live post URL (optional):", "") || "";
    try {
      await api.post(`/admin/social-drafts/${draftId}/mark-posted`, { posted_url, channel }, authHeaders);
      toast.success("Marked posted.");
      loadDrafts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const dismissDraft = async (draftId) => {
    if (!window.confirm("Dismiss this draft? It will be hidden.")) return;
    try {
      await api.post(`/admin/social-drafts/${draftId}/dismiss`, {}, authHeaders);
      toast.success("Dismissed.");
      loadDrafts();
    } catch (err) {
      toast.error("Failed");
    }
  };

  const publishDraft = async (draftId) => {
    if (!window.confirm("Publish via Zapier? This fires the webhook which fans out to all your connected channels (Buffer / IG / TikTok / X / Threads / FB).")) return;
    try {
      await api.post(`/admin/social-drafts/${draftId}/publish`, {}, authHeaders);
      toast.success("Published → Zapier webhook fired.");
      loadDrafts();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Publish failed";
      if (detail.includes("ZAPIER_WEBHOOK_URL")) {
        toast.error("Add ZAPIER_WEBHOOK_URL to backend/.env first.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Publish failed");
      }
    }
  };

  const copyText = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success("Caption copied — now hop to IG / Threads.");
  };

  if (!authed) {
    return (
      <div className="grain min-h-screen flex items-center justify-center px-6" data-testid="admin-login">
        <div className="card-noir p-8 max-w-md w-full">
          <div className="flex items-center gap-2 mb-2">
            <Shield size={18} className="text-[#8b0000]" />
            <div className="font-hand text-[#d4af37] rotate-[-1deg] text-xl">staff only</div>
          </div>
          <h1 className="font-display text-3xl mb-6">wasntme.party admin</h1>
          <input type="password" className="input-noir mb-4" placeholder="admin token"
            value={token} onChange={(e) => setToken(e.target.value)} data-testid="admin-token-input" />
          <button className="btn-primary w-full justify-center" onClick={loadLeads} disabled={loading}
            data-testid="admin-login-btn">
            {loading ? <Loader2 size={18} className="animate-spin" /> : "Unlock"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="grain min-h-screen" data-testid="admin-dashboard">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="font-hand text-[#d4af37] rotate-[-1deg] text-2xl mb-2">backstage</div>
        <h1 className="font-display text-4xl mb-10">Lead Vault</h1>

        <div className="grid md:grid-cols-3 gap-4 mb-10">
          <div className="card-noir p-6">
            <div className="flex items-center gap-2 mb-2">
              <Users size={16} className="text-[#8b0000]" />
              <div className="text-xs tracking-widest text-[#a39797]">TOTAL LEADS</div>
            </div>
            <div className="font-display text-4xl text-[#f4f0ec]" data-testid="total-leads">{data?.total || 0}</div>
          </div>
          <div className="card-noir p-6 col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <Mail size={16} className="text-[#d4af37]" />
              <div className="text-xs tracking-widest text-[#a39797]">BY CASE INTEREST</div>
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(data?.by_case_interest || {}).map(([k, v]) => (
                <span key={k} className="text-xs px-3 py-1.5 rounded-full border border-[#2d2424] text-[#a39797]">
                  <span className="text-[#f4f0ec]">{k || "(any)"}</span> · {v}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Comp a Crew Pack — admin shortcut (streamers have the same tool at /streamer/outreach) */}
        <AdminCompCrew authHeaders={authHeaders} />

        {/* Society Spotlight — pick / override this week's featured detective */}
        <AdminSpotlight authHeaders={authHeaders} />

        {/* App settings — feature flags, seasonal pin, mood mix URLs */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl">Site settings</h2>
          <button onClick={loadSettings} className="btn-ghost text-xs" data-testid="settings-refresh">
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
        <div className="card-noir p-5 mb-10" data-testid="settings-panel">
          {!settings ? (
            <div className="text-sm text-[#a39797]">Loading settings…</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Seasonal homepage */}
              <div data-testid="settings-homepage">
                <div className="text-xs tracking-widest text-[#d4af37] mb-3">SEASONAL HOMEPAGE</div>
                <label className="flex items-center gap-3 mb-3 cursor-pointer">
                  <input type="checkbox"
                    checked={!!settings.homepage?.enabled}
                    onChange={(e) => setSettings((s) => ({ ...s, homepage: { ...s.homepage, enabled: e.target.checked } }))}
                    className="accent-[#8b0000]"
                    data-testid="settings-homepage-toggle" />
                  <span className="text-sm text-[#f4f0ec]">Enable seasonal banner + hero override</span>
                </label>
                <label className="block text-xs tracking-widest text-[#a39797] mb-2">PIN SEASON (overrides date)</label>
                <select className="input-noir text-sm"
                  value={settings.homepage?.active_override_season_id || ""}
                  onChange={(e) => setSettings((s) => ({ ...s, homepage: { ...s.homepage, active_override_season_id: e.target.value } }))}
                  data-testid="settings-season-override">
                  <option value="">Auto (by visitor's local date)</option>
                  {(settings.homepage?.seasons || []).map((s) => (
                    <option key={s.id} value={s.id}>{s.label} ({s.start} → {s.end})</option>
                  ))}
                </select>
                <button className="btn-primary text-sm mt-4 disabled:opacity-50"
                  disabled={savingSettings}
                  onClick={() => saveSetting("homepage", settings.homepage)}
                  data-testid="settings-homepage-save">
                  Save homepage
                </button>
              </div>

              {/* Mood Mix */}
              <div data-testid="settings-mood-mix">
                <div className="text-xs tracking-widest text-[#d4af37] mb-3">MOOD MIX PLAYLISTS</div>
                <label className="flex items-center gap-3 mb-3 cursor-pointer">
                  <input type="checkbox"
                    checked={!!settings.mood_mix?.enabled}
                    onChange={(e) => setSettings((s) => ({ ...s, mood_mix: { ...s.mood_mix, enabled: e.target.checked } }))}
                    className="accent-[#8b0000]"
                    data-testid="settings-mood-mix-toggle" />
                  <span className="text-sm text-[#f4f0ec]">Show quick-pick chips on ScreenKit</span>
                </label>
                <div className="space-y-3">
                  {(settings.mood_mix?.playlists || []).map((p, i) => (
                    <div key={p.id} className="border border-[#2d2424] rounded p-3" data-testid={`settings-playlist-${p.id}`}>
                      <div className="text-xs text-[#f4f0ec] font-semibold mb-1">{p.label}</div>
                      <input type="url"
                        value={p.url}
                        onChange={(e) => {
                          const next = [...settings.mood_mix.playlists];
                          next[i] = { ...next[i], url: e.target.value };
                          setSettings((s) => ({ ...s, mood_mix: { ...s.mood_mix, playlists: next } }));
                        }}
                        className="input-noir text-xs font-mono"
                        placeholder="https://open.spotify.com/playlist/..." />
                    </div>
                  ))}
                </div>
                <button className="btn-primary text-sm mt-4 disabled:opacity-50"
                  disabled={savingSettings}
                  onClick={() => saveSetting("mood_mix", settings.mood_mix)}
                  data-testid="settings-mood-mix-save">
                  Save mood mix
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Enterprise Quotes */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl">Enterprise quotes</h2>
          <button onClick={loadQuotes} className="btn-ghost text-xs" data-testid="quotes-refresh">
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
        <div className="card-noir p-5 mb-10" data-testid="quotes-panel">
          <div className="grid grid-cols-2 gap-4 mb-5 text-sm">
            <div>
              <div className="text-xs tracking-widest text-[#a39797]">OPEN QUOTES</div>
              <div className="font-display text-3xl text-[#f4f0ec]" data-testid="quotes-total">{quotesData?.total || 0}</div>
            </div>
            <div>
              <div className="text-xs tracking-widest text-[#a39797]">PIPELINE</div>
              <div className="font-display text-3xl text-[#d4af37]" data-testid="quotes-pipeline">
                ${((quotesData?.total_amount_cents || 0) / 100).toLocaleString()}
              </div>
            </div>
          </div>
          <div className="divide-y divide-[#2d2424]" data-testid="quotes-list">
            {(quotesData?.quotes || []).map((q) => (
              <div key={q.id} className="py-4 flex items-start gap-4" data-testid={`quote-row-${q.id}`}>
                {q.logo_storage_path ? (
                  <img src={logoUrl(q.logo_storage_path)} alt="Logo"
                    className="w-14 h-14 object-contain rounded border border-[#2d2424] bg-[#0a0908] p-1 flex-shrink-0" />
                ) : (
                  <div className="w-14 h-14 rounded border border-[#2d2424] bg-[#0a0908] flex items-center justify-center flex-shrink-0">
                    <Building2 size={18} className="text-[#2d2424]" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center flex-wrap gap-2">
                    <div className="font-display text-base text-[#f4f0ec]">{q.company}</div>
                    {q.brand_hex && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-[#a39797]">
                        <span className="w-3 h-3 rounded-sm border border-[#2d2424]"
                          style={{ backgroundColor: q.brand_hex }} />
                        {q.brand_hex}
                      </span>
                    )}
                    <span className={`text-[10px] tracking-widest uppercase px-2 py-[1px] rounded-full border ${q.status === "sent" ? "border-[#d4af37] text-[#d4af37]" : "border-[#2d2424] text-[#a39797]"}`}>
                      {q.status}
                    </span>
                  </div>
                  <div className="text-xs text-[#a39797] mt-1 truncate">
                    {q.contact_name} · {q.work_email} · {q.people_count}p · {q.party}
                  </div>
                  <div className="text-xs text-[#a39797] mt-0.5">{(q.created_at || "").slice(0, 16).replace("T", " ")}</div>
                </div>
                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                  <div className="font-display text-lg text-[#f4f0ec]">
                    ${((q.amount_due_cents || 0) / 100).toLocaleString()}
                  </div>
                  <div className="text-[10px] text-[#a39797]">NET {q.net_days}</div>
                  <div className="flex gap-2">
                    {q.hosted_invoice_url && (
                      <a href={q.hosted_invoice_url} target="_blank" rel="noopener noreferrer"
                        className="btn-ghost text-[11px]" data-testid={`quote-view-${q.id}`}>
                        <ExternalLink size={11} /> View
                      </a>
                    )}
                    <button onClick={() => resendQuote(q.id)} disabled={resendingId === q.id}
                      className="btn-primary text-[11px] disabled:opacity-50"
                      data-testid={`quote-resend-${q.id}`}>
                      {resendingId === q.id
                        ? <><Loader2 size={11} className="animate-spin" /> Sending</>
                        : <><Send size={11} /> Resend</>}
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {(quotesData?.quotes || []).length === 0 && (
              <div className="text-center text-[#a39797] py-6 text-sm">No enterprise quotes yet.</div>
            )}
          </div>
        </div>

        {/* Blast form */}
        <h2 className="font-display text-2xl mb-4">Send a case drop email</h2>
        <form onSubmit={sendBlast} className="card-noir p-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs tracking-widest text-[#a39797] mb-2">FILTER BY CASE INTEREST (optional)</label>
              <input className="input-noir" placeholder="e.g. dinner_for_four — leave blank for all"
                value={blast.case_interest}
                onChange={(e) => setBlast({ ...blast, case_interest: e.target.value })}
                data-testid="blast-case-interest" />
              <div className="text-xs text-[#a39797] mt-1">will send to ~{estimatedRecipients()} leads</div>
            </div>
            <div>
              <label className="block text-xs tracking-widest text-[#a39797] mb-2">MODE</label>
              <div className="flex gap-2">
                <button type="button"
                  onClick={() => setBlast({ ...blast, test_only: true })}
                  className={`flex-1 text-xs px-3 py-2 rounded-full border transition-colors ${blast.test_only ? "border-[#d4af37] text-[#d4af37]" : "border-[#2d2424] text-[#a39797]"}`}
                  data-testid="blast-mode-test">Test send</button>
                <button type="button"
                  onClick={() => setBlast({ ...blast, test_only: false })}
                  className={`flex-1 text-xs px-3 py-2 rounded-full border transition-colors ${!blast.test_only ? "border-[#8b0000] text-[#8b0000]" : "border-[#2d2424] text-[#a39797]"}`}
                  data-testid="blast-mode-live">Send to all</button>
              </div>
              {blast.test_only && (
                <input className="input-noir mt-2 text-sm" type="email" placeholder="test send to..."
                  value={blast.admin_test_to}
                  onChange={(e) => setBlast({ ...blast, admin_test_to: e.target.value })}
                  data-testid="blast-test-to" />
              )}
            </div>
          </div>
          <div>
            <label className="block text-xs tracking-widest text-[#a39797] mb-2">SUBJECT</label>
            <input className="input-noir" value={blast.subject}
              onChange={(e) => setBlast({ ...blast, subject: e.target.value })}
              placeholder="New Case Dropped: #006 Who Shot Cupid?"
              data-testid="blast-subject" />
          </div>
          <div>
            <label className="block text-xs tracking-widest text-[#a39797] mb-2">HTML BODY</label>
            <textarea className="input-noir min-h-[200px] font-mono text-sm" value={blast.html}
              onChange={(e) => setBlast({ ...blast, html: e.target.value })}
              placeholder={`<p>Hi,</p>\n<p>Case #006 just dropped at <a href="https://wasntme.party">wasntme.party</a>.</p>\n<p>Use code <strong>VAULT5</strong> for $5 off.</p>`}
              data-testid="blast-html" />
            <div className="text-xs text-[#a39797] mt-1">All links must be absolute https. No forms. Our safety-gate rejects anything that looks phishy.</div>
          </div>
          <button type="submit" className="btn-primary" disabled={sending} data-testid="blast-submit">
            {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
            {sending ? "Sending..." : blast.test_only ? "Send test" : "Send to all leads"}
          </button>
        </form>

        {/* Jev · AI-flagged items for review */}
        <h2 className="font-display text-2xl mt-10 mb-2">🧠 Jev · needs your eyes</h2>
        {jevFlags && (
          <div className="card-noir p-5 mb-10" data-testid="jev-flags-card">
            <div className="flex items-center gap-4 text-sm mb-4">
              <div className="text-[#a39797]">
                Flagged intake: <span className="text-[#8b0000] font-bold" data-testid="jev-flagged-count">{jevFlags.counts.flagged_intakes}</span>
              </div>
              <div className="text-[#a39797]">
                Survey comments needing attention: <span className="text-[#d4af37] font-bold" data-testid="jev-attention-count">{jevFlags.counts.attention_surveys}</span>
              </div>
            </div>

            {jevFlags.flagged_intakes.length > 0 && (
              <>
                <div className="text-xs tracking-widest text-[#8b0000] mb-2">INTAKE · NEEDS REVIEW</div>
                <div className="space-y-2 mb-6">
                  {jevFlags.flagged_intakes.map((it, i) => (
                    <div key={i} className="p-3 border border-[#2d2424] rounded bg-black/30" data-testid={`jev-intake-${i}`}>
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                          <div className="text-sm text-[#f4f0ec]">{it.name || "unnamed"}</div>
                          <div className="text-xs text-[#a39797]">
                            {it.verdict} · conf={Number(it.confidence || 0).toFixed(2)}
                            {it.pii_probability > 0 && ` · pii_p=${Number(it.pii_probability).toFixed(2)}`}
                          </div>
                          <div className="text-xs text-[#a39797] mt-1 italic">{it.reason}</div>
                        </div>
                        <a className="btn-ghost text-xs"
                           href={`/invite/${it.group_id}`} target="_blank" rel="noopener noreferrer">
                          Open invite
                        </a>
                      </div>
                      <div className="text-xs text-[#f4f0ec] mt-2 space-y-0.5">
                        <div><span className="text-[#8b0000]">secret:</span> {it.secret}</div>
                        <div><span className="text-[#8b0000]">signature:</span> {it.signature}</div>
                        {it.off_limits && <div><span className="text-[#8b0000]">off-limits:</span> {it.off_limits}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {jevFlags.attention_surveys.length > 0 && (
              <>
                <div className="text-xs tracking-widest text-[#d4af37] mb-2">SURVEY COMMENTS · YOUR ATTENTION</div>
                <div className="space-y-2">
                  {jevFlags.attention_surveys.map((s, i) => (
                    <div key={i} className="p-3 border border-[#2d2424] rounded bg-black/30" data-testid={`jev-survey-${i}`}>
                      <div className="text-xs text-[#a39797] mb-1">
                        {s.triage?.category || "?"} · sentiment={Number(s.triage?.sentiment || 0).toFixed(2)} · rating={s.rating}
                      </div>
                      <div className="text-sm text-[#f4f0ec]">{s.comment}</div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {jevFlags.counts.flagged_intakes === 0 && jevFlags.counts.attention_surveys === 0 && (
              <div className="text-sm text-[#a39797]" data-testid="jev-all-clear">
                All clear. Jev will surface anything that needs your eyes here.
              </div>
            )}
          </div>
        )}

        {/* Case Maker analytics */}
        <h2 className="font-display text-2xl mt-10 mb-2">🔨 Case Maker · drafts, orders, revenue</h2>
        {makeAnalytics && (
          <div className="card-noir p-5 mb-10" data-testid="make-analytics-card">
            <div className="grid md:grid-cols-4 gap-4 mb-5">
              <AnalyticsStat label="DRAFTS" value={makeAnalytics.totals.drafts} testid="make-stat-drafts" />
              <AnalyticsStat label="PAID" value={makeAnalytics.totals.orders_paid} testid="make-stat-paid" />
              <AnalyticsStat label="AWAITING" value={makeAnalytics.totals.orders_awaiting} testid="make-stat-awaiting" />
              <AnalyticsStat label="REVENUE"
                value={`$${(makeAnalytics.totals.revenue_cents / 100).toFixed(2)}`}
                testid="make-stat-revenue" />
            </div>
            <div className="text-xs text-[#a39797] mb-5">
              Draft → paid conversion: <b className="text-[#f4f0ec]">{(makeAnalytics.totals.draft_to_paid_rate * 100).toFixed(1)}%</b>
            </div>

            <div className="text-xs tracking-widest text-[#a39797] mb-2">BY ENGINE</div>
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm" data-testid="make-by-engine-table">
                <thead>
                  <tr className="text-left text-[#a39797] border-b border-[#2d2424]">
                    <th className="py-2 pr-3">Engine</th>
                    <th className="py-2 px-3">Drafts</th>
                    <th className="py-2 px-3">Paid</th>
                    <th className="py-2 px-3">Awaiting</th>
                    <th className="py-2 px-3">Revenue</th>
                    <th className="py-2 pl-3">Conv.</th>
                  </tr>
                </thead>
                <tbody>
                  {makeAnalytics.by_engine.map((row) => {
                    const conv = row.drafts ? (row.paid / row.drafts * 100).toFixed(1) : "—";
                    return (
                      <tr key={row.engine_id} className="border-b border-[#2d2424]"
                        data-testid={`make-engine-row-${row.engine_id}`}>
                        <td className="py-2 pr-3 text-[#f4f0ec]">{row.engine_id}</td>
                        <td className="py-2 px-3">{row.drafts}</td>
                        <td className="py-2 px-3 text-[#9fd79f]">{row.paid}</td>
                        <td className="py-2 px-3">{row.awaiting_payment}</td>
                        <td className="py-2 px-3 text-[#d4af37]">${(row.revenue_cents / 100).toFixed(2)}</td>
                        <td className="py-2 pl-3">{conv === "—" ? conv : `${conv}%`}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="text-xs tracking-widest text-[#a39797] mb-2">RECENT ORDERS (25 MAX)</div>
            {makeAnalytics.recent_orders.length === 0 ? (
              <div className="text-sm text-[#a39797]" data-testid="make-no-orders">No orders yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="make-recent-table">
                  <thead>
                    <tr className="text-left text-[#a39797] border-b border-[#2d2424]">
                      <th className="py-2 pr-3">When</th>
                      <th className="py-2 px-3">Host</th>
                      <th className="py-2 px-3">Email</th>
                      <th className="py-2 px-3">Engine</th>
                      <th className="py-2 px-3">Status</th>
                      <th className="py-2 pl-3">$</th>
                    </tr>
                  </thead>
                  <tbody>
                    {makeAnalytics.recent_orders.map((o) => (
                      <tr key={o.order_id} className="border-b border-[#2d2424]"
                        data-testid={`make-order-row-${o.order_id}`}>
                        <td className="py-2 pr-3 text-[#a39797] text-xs">{(o.created_at || "").slice(0, 10)}</td>
                        <td className="py-2 px-3 text-[#f4f0ec]">{o.host_name || "—"}</td>
                        <td className="py-2 px-3 text-xs">{o.host_email}</td>
                        <td className="py-2 px-3 text-xs">{o.engine_id || "—"}</td>
                        <td className={`py-2 px-3 text-xs ${o.status === "paid" ? "text-[#9fd79f]" : "text-[#a39797]"}`}>
                          {o.status}
                        </td>
                        <td className="py-2 pl-3 text-[#d4af37]">${(o.amount_cents / 100).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* SMS / Twilio usage */}
        <h2 className="font-display text-2xl mt-10 mb-2">📱 Twilio · SMS usage</h2>
        {smsUsage && (
          <div className="card-noir p-5 mb-10" data-testid="sms-usage-card">
            <div className="grid md:grid-cols-4 gap-4 mb-4">
              <div>
                <div className="text-xs tracking-widest text-[#a39797]">STATUS</div>
                <div className={`font-display text-xl mt-1 ${smsUsage.configured ? "text-[#d4af37]" : "text-[#a39797]"}`}
                     data-testid="sms-configured">
                  {smsUsage.configured ? "Configured" : "Not configured"}
                </div>
                <div className="text-xs text-[#a39797] mt-1">
                  Verify (OTP): {smsUsage.verify_configured ? "✓" : "—"}
                  &nbsp;·&nbsp; Channel: {smsUsage.prefer_whatsapp ? "WhatsApp" : "SMS"}
                </div>
              </div>
              <div>
                <div className="text-xs tracking-widest text-[#a39797]">SENT ({smsUsage.month_key})</div>
                <div className="font-display text-3xl text-[#f4f0ec] mt-1" data-testid="sms-sent">
                  {smsUsage.sent_this_month}
                </div>
              </div>
              <div>
                <div className="text-xs tracking-widest text-[#a39797]">REMAINING</div>
                <div className="font-display text-3xl text-[#d4af37] mt-1" data-testid="sms-remaining">
                  {smsUsage.remaining}
                </div>
                <div className="text-xs text-[#a39797] mt-1">cap: {smsUsage.monthly_cap}/mo</div>
              </div>
              <div>
                <div className="text-xs tracking-widest text-[#a39797]">BY STATUS</div>
                <div className="text-xs text-[#f4f0ec] mt-1 space-y-0.5">
                  {Object.entries(smsUsage.by_status || {}).map(([k, v]) => (
                    <div key={k}><span className="text-[#a39797]">{k}:</span> {v}</div>
                  ))}
                  {Object.keys(smsUsage.by_status || {}).length === 0 && (
                    <div className="text-[#a39797]">no sends yet</div>
                  )}
                </div>
              </div>
            </div>
            {smsUsage.recent && smsUsage.recent.length > 0 && (
              <div>
                <div className="text-xs tracking-widest text-[#8b0000] mb-2">RECENT (last 50)</div>
                <div className="max-h-64 overflow-auto border border-[#2d2424] rounded">
                  <table className="w-full text-xs">
                    <thead className="bg-[#1a1413] text-[#a39797] sticky top-0">
                      <tr>
                        <th className="text-left px-2 py-1">kind</th>
                        <th className="text-left px-2 py-1">to</th>
                        <th className="text-left px-2 py-1">status</th>
                        <th className="text-left px-2 py-1">channel</th>
                        <th className="text-left px-2 py-1">when</th>
                      </tr>
                    </thead>
                    <tbody>
                      {smsUsage.recent.map((row, i) => (
                        <tr key={i} className="border-t border-[#2d2424]" data-testid={`sms-row-${i}`}>
                          <td className="px-2 py-1 text-[#d4af37]">{row.kind}</td>
                          <td className="px-2 py-1 text-[#f4f0ec] font-mono">{row.to}</td>
                          <td className={`px-2 py-1 ${row.status === "sent" ? "text-[#d4af37]" : row.status === "failed" ? "text-[#8b0000]" : "text-[#a39797]"}`}>
                            {row.status}
                          </td>
                          <td className="px-2 py-1 text-[#a39797]">{row.channel}</td>
                          <td className="px-2 py-1 text-[#a39797]">{(row.created_at || "").slice(5, 16).replace("T", " ")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Social share autodrafts */}
        <h2 className="font-display text-2xl mt-10 mb-2">🎃 Social autodrafts</h2>
        <p className="text-[#a39797] text-sm mb-4">
          Auto-generated IG + Threads captions and share images for every horror-case playtester redemption.
          Review, copy, post, mark posted.{" "}
          {drafts?.counts && (
            <span className="text-[#d4af37]">
              {drafts.counts.draft || 0} draft · {drafts.counts.posted || 0} posted · {drafts.counts.dismissed || 0} dismissed
            </span>
          )}
        </p>
        <div className="space-y-4 mb-10" data-testid="social-drafts-list">
          {(drafts?.drafts || []).filter((d) => d.status === "draft").length === 0 ? (
            <div className="card-noir p-6 text-center text-[#a39797] text-sm" data-testid="no-drafts">
              No pending drafts. Drafts auto-generate when someone redeems PLAYTEST50 on a horror case.
            </div>
          ) : (
            (drafts?.drafts || []).filter((d) => d.status === "draft").map((d) => (
              <div key={d.id} className="card-noir p-5" data-testid={`draft-${d.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="text-xs tracking-widest text-[#8b0000]">#{d.case_number} · {d.case_title}</div>
                    <div className="text-xs text-[#a39797] mt-1">
                      queued {(d.created_at || "").slice(0, 16).replace("T", " ")} · {d.remaining_spots_at_draft} playtest spots left at draft time
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <a href={d.images.story} target="_blank" rel="noopener noreferrer" className="btn-ghost text-xs"
                       data-testid={`download-story-${d.id}`}>Download 9:16</a>
                    <a href={d.images.square} target="_blank" rel="noopener noreferrer" className="btn-ghost text-xs"
                       data-testid={`download-square-${d.id}`}>Download 1:1</a>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-xs tracking-widest text-[#d4af37]">INSTAGRAM</div>
                      <button className="btn-ghost text-xs" onClick={() => copyText(d.captions.ig)}
                              data-testid={`copy-ig-${d.id}`}>Copy IG</button>
                    </div>
                    <pre className="text-xs text-[#f4f0ec] bg-black/40 p-3 whitespace-pre-wrap font-sans leading-relaxed max-h-56 overflow-auto">{d.captions.ig}</pre>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-xs tracking-widest text-[#d4af37]">THREADS</div>
                      <button className="btn-ghost text-xs" onClick={() => copyText(d.captions.threads)}
                              data-testid={`copy-threads-${d.id}`}>Copy Threads</button>
                    </div>
                    <pre className="text-xs text-[#f4f0ec] bg-black/40 p-3 whitespace-pre-wrap font-sans leading-relaxed max-h-56 overflow-auto">{d.captions.threads}</pre>
                  </div>
                </div>
                <div className="flex gap-2 mt-4 flex-wrap">
                  <button className="btn-primary text-sm" onClick={() => publishDraft(d.id)}
                          data-testid={`publish-${d.id}`}>
                    ⚡ Approve &amp; publish
                  </button>
                  <button className="btn-ghost text-sm" onClick={() => markPosted(d.id, "both")}
                          data-testid={`mark-posted-${d.id}`}>Mark posted manually</button>
                  <button className="btn-ghost text-sm" onClick={() => dismissDraft(d.id)}
                          data-testid={`dismiss-${d.id}`}>Dismiss</button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Recent leads list */}
        <h2 className="font-display text-2xl mt-10 mb-4">Recent leads</h2>
        <div className="card-noir p-4">
          <div className="divide-y divide-[#2d2424]">
            {(data?.leads || []).slice(0, 30).map((l) => (
              <div key={`${l.email}|${l.case_interest || ""}|${l.created_at || ""}`} className="py-3 flex items-center justify-between text-sm">
                <div className="text-[#f4f0ec]">{l.email}</div>
                <div className="flex gap-2 items-center text-xs text-[#a39797]">
                  <span className="px-2 py-1 rounded-full border border-[#2d2424]">{l.case_interest || "any"}</span>
                  <span>{l.source}</span>
                  <span>{(l.created_at || "").slice(0, 10)}</span>
                </div>
              </div>
            ))}
            {(data?.leads || []).length === 0 && (
              <div className="text-center text-[#a39797] py-6">No leads yet.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;

const AnalyticsStat = ({ label, value, testid }) => (
  <div data-testid={testid}>
    <div className="text-xs tracking-widest text-[#a39797]">{label}</div>
    <div className="font-display text-3xl mt-1 text-[#f4f0ec]">{value}</div>
  </div>
);

const AdminCompCrew = ({ authHeaders }) => {  const [packOptions, setPackOptions] = useState([]);
  const [form, setForm] = useState({ pack_id: "", recipient_email: "", recipient_name: "", note: "" });
  const [sending, setSending] = useState(false);
  const [lastComp, setLastComp] = useState(null);

  useEffect(() => {
    api.get("/packs")
      .then((r) => {
        const opts = (r.data?.packs || []).map((p) => ({ id: p.id, label: p.label }));
        setPackOptions(opts);
        setForm((f) => ({ ...f, pack_id: f.pack_id || (opts[0]?.id || "") }));
      })
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const { data: comp } = await api.post("/comp-crew", form, authHeaders);
      setLastComp(comp);
      setForm({ ...form, recipient_email: "", recipient_name: "", note: "" });
      toast.success("Comp sent.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Comp failed");
    } finally {
      setSending(false);
    }
  };

  const copy = async (txt) => {
    try { await navigator.clipboard.writeText(txt); toast.success("Copied."); } catch {}
  };

  return (
    <div className="mb-10" data-testid="admin-comp-crew-panel">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl">Comp a crew pack</h2>
        <span className="text-[10px] tracking-[0.3em] text-[#5a5050]">ADMIN SHORTCUT</span>
      </div>
      <div className="card-noir p-5">
        <p className="text-xs text-[#a39797] mb-4">
          Gifts a full pack (price $0, status paid) to a recipient. They get a redemption URL by email AND you can copy-paste it into a DM.
          Approved streamers have the same tool at <code className="text-[#d4af37]">/streamer/outreach</code> with a 3/day cap.
        </p>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] tracking-[0.2em] text-[#a39797] block mb-1">PACK</label>
              <select className="input-noir w-full" value={form.pack_id}
                onChange={(e) => setForm({ ...form, pack_id: e.target.value })}
                data-testid="admin-comp-pack">
                {packOptions.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] tracking-[0.2em] text-[#a39797] block mb-1">RECIPIENT EMAIL</label>
              <input type="email" required className="input-noir w-full"
                placeholder="them@wherever.com"
                value={form.recipient_email}
                onChange={(e) => setForm({ ...form, recipient_email: e.target.value })}
                data-testid="admin-comp-email" />
            </div>
          </div>
          <div>
            <label className="text-[10px] tracking-[0.2em] text-[#a39797] block mb-1">RECIPIENT NAME (OPTIONAL)</label>
            <input type="text" maxLength={80} className="input-noir w-full"
              placeholder="@handle or first name"
              value={form.recipient_name}
              onChange={(e) => setForm({ ...form, recipient_name: e.target.value })}
              data-testid="admin-comp-name" />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.2em] text-[#a39797] block mb-1">NOTE (OPTIONAL)</label>
            <textarea rows={2} maxLength={400} className="input-noir w-full"
              placeholder="Short note that goes in the invite email."
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              data-testid="admin-comp-note" />
          </div>
          <button type="submit" disabled={sending} className="btn-primary text-sm" data-testid="admin-comp-submit">
            {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
            {sending ? "Minting…" : "SEND COMP"}
          </button>
        </form>

        {lastComp && (
          <div className="mt-4 rounded-md border border-[#d4af37]/40 bg-[#1a1515] p-3" data-testid="admin-comp-success">
            <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mb-2">COMP READY</div>
            <div className="font-mono text-[11px] text-[#d4d0cc] break-all mb-2">{lastComp.redeem_url}</div>
            <button onClick={() => copy(lastComp.redeem_url)} className="btn-ghost text-xs" data-testid="admin-comp-copy">
              Copy URL
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const AdminSpotlight = ({ authHeaders }) => {
  const [spot, setSpot]       = useState(null);
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm]       = useState({ detective_number: "", theory: "", note: "" });
  const [saving, setSaving]   = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/society/spotlight");
      setSpot(r.data?.spotlight || null);
      setStats(r.data?.stats || null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const pin = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      await api.put("/admin/society/spotlight", {
        detective_number: parseInt(form.detective_number, 10),
        theory: form.theory,
        note:   form.note,
      }, authHeaders);
      toast.success("Spotlight pinned.");
      setForm({ detective_number: "", theory: "", note: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Pin failed");
    } finally {
      setSaving(false);
    }
  };

  const unpin = async () => {
    if (!window.confirm("Clear the admin pin and revert to auto-pick?")) return;
    try {
      await api.delete("/admin/society/spotlight", authHeaders);
      toast.success("Pin cleared — auto-pick restored.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Clear failed");
    }
  };

  return (
    <div className="mb-10" data-testid="admin-spotlight-panel">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl">Society spotlight</h2>
        <span className="text-[10px] tracking-[0.3em] text-[#5a5050]">AUTO-PICK · ADMIN CAN OVERRIDE</span>
      </div>
      <div className="card-noir p-5">
        {loading ? (
          <div className="text-sm text-[#a39797]">Loading spotlight…</div>
        ) : (
          <>
            {spot ? (
              <div className="mb-5" data-testid="admin-spotlight-current">
                <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mb-1">
                  CURRENT ·  {spot.source === "admin" ? "CURATED BY YOU" : "AUTO-PICKED"}
                </div>
                <div className="font-display text-2xl mb-1">Detective #{spot.detective_number}</div>
                {spot.source === "auto" && (
                  <div className="text-xs text-[#a39797]">
                    {spot.correct} crack{spot.correct !== 1 ? "s" : ""} this week · score {spot.score}
                  </div>
                )}
                {spot.theory && (
                  <div className="text-sm text-[#d4d0cc] mt-2 italic">"{spot.theory}"</div>
                )}
                {spot.source === "admin" && (
                  <button onClick={unpin} className="btn-ghost text-xs mt-3" data-testid="admin-spotlight-clear">
                    Clear pin & revert to auto-pick
                  </button>
                )}
              </div>
            ) : (
              <div className="text-xs text-[#a39797] mb-4">
                No spotlight this week. Pin one below, or let auto-pick fill it when the first correct crack lands.
              </div>
            )}
            {stats && (
              <div className="text-[10px] text-[#5a5050] mb-5">
                This week: {stats.plays_this_week} plays · {stats.cracked_this_week} cracked · {stats.accuracy_this_week}% accuracy · {stats.detectives_total} detectives on record
              </div>
            )}
            <form onSubmit={pin} className="space-y-3">
              <div className="grid sm:grid-cols-[200px_1fr] gap-3">
                <div>
                  <label className="text-[10px] tracking-[0.2em] text-[#a39797] block mb-1">DETECTIVE #</label>
                  <input type="number" min={1} required className="input-noir w-full"
                    placeholder="42"
                    value={form.detective_number}
                    onChange={(e) => setForm({ ...form, detective_number: e.target.value })}
                    data-testid="admin-spotlight-number" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] text-[#a39797] block mb-1">FEATURED THEORY / QUOTE</label>
                  <input type="text" maxLength={400} className="input-noir w-full"
                    placeholder="They were lying about the keycard log."
                    value={form.theory}
                    onChange={(e) => setForm({ ...form, theory: e.target.value })}
                    data-testid="admin-spotlight-theory" />
                </div>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] text-[#a39797] block mb-1">INTERNAL NOTE (OPTIONAL — public)</label>
                <input type="text" maxLength={400} className="input-noir w-full"
                  placeholder="With their permission — featured on TikTok 10/12"
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  data-testid="admin-spotlight-note" />
              </div>
              <button type="submit" disabled={saving} className="btn-primary text-sm" data-testid="admin-spotlight-pin">
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Users size={13} />}
                {saving ? "Pinning…" : "PIN THIS DETECTIVE"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};
